import { getModalContext, type ModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
import type { Component } from 'svelte';
import type { MenuItem } from './Menu.svelte';
import Menu from './Menu.svelte';

// Approximate rendered dimensions used for overflow detection.
// These must stay in sync with Menu.svelte styles.
const MENU_ITEM_HEIGHT = 44; // padding (0.5rem * 2 = 16px) + line-height (~1.5rem = 24px) + gap (0.25rem = 4px)
const MENU_OVERHEAD = 8; // container padding (0.25rem * 2 = 8px); border is an inset shadow, no layout size
const MENU_MIN_WIDTH = 200; // matches min-width in Menu.svelte
const SCREEN_MARGIN = 8; // minimum gap from screen edges

/**
 * Opens a component as a fixed modal anchored to a node, flipping above the
 * anchor or right-aligning when the estimated dimensions would overflow the screen.
 */
export function openAnchoredModal<Props extends Record<string, any>>(
	modalCtx: ModalContext,
	node: HTMLElement,
	component: Component<Props>,
	props: Props,
	{
		estimatedHeight,
		width,
		offset = 8
	}: { estimatedHeight: number; width: number; offset?: number | { x: number; y: number } }
): string {
	const offsetX = typeof offset === 'number' ? offset : offset.x;
	const offsetY = typeof offset === 'number' ? offset : offset.y;
	const rect = node.getBoundingClientRect();

	// Only flip above the anchor if it doesn't fit below AND there's actually
	// more room above than below — otherwise a trigger near the top of the
	// screen (e.g. a sidebar header) flips a tall list off the top of the
	// viewport instead of just leaving it anchored below, scrollable.
	const spaceBelow = window.innerHeight - rect.bottom - offsetY;
	const spaceAbove = rect.top - offsetY;
	const tooLow = estimatedHeight > spaceBelow - SCREEN_MARGIN && spaceAbove > spaceBelow;
	const tooRight = rect.left + offsetX + width > window.innerWidth - SCREEN_MARGIN;

	const anchor: 'left' | 'right' = tooRight ? 'right' : 'left';
	const x = tooRight ? rect.right - offsetX : rect.left + offsetX;
	const y = tooLow ? undefined : rect.bottom + offsetY;
	const bottom = tooLow ? window.innerHeight - rect.top + offsetY : undefined;

	return modalCtx.openModal(component, props, { x, y, bottom, anchor, appearance: 'fixed', width });
}

export function menu({
	items = [],
	offset = 8,
	flex = false
}: {
	items: MenuItem[];
	offset?: number | { x: number; y: number };
	flex?: boolean;
}) {
	const modalCtx = getModalContext();

	return (node: HTMLElement) => {
		node.style.cursor = 'pointer';

		const clickHandler = (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			const offsetX = typeof offset === 'number' ? offset : offset.x;
			const estimatedHeight = items.length * MENU_ITEM_HEIGHT + MENU_OVERHEAD;
			const width = flex ? node.getBoundingClientRect().width - 2 * offsetX : MENU_MIN_WIDTH;

			openAnchoredModal(modalCtx, node, Menu, { items }, { estimatedHeight, width, offset });
		};

		node.addEventListener('click', clickHandler);
		return () => node.removeEventListener('click', clickHandler);
	};
}
